import { useAppConfig } from "~/app/config/useAppConfig";
import { type FC, memo, type ReactNode } from "react";

import { type PriceData } from "~/domains/pricing/types/PriceData";
import * as PriceUtils from "../../../../utils/PriceUtils";
import * as WeightUtils from "../../../../utils/WeightUtils";
import { type WeightData } from "~/domains/product/types/WeightData";
import Spacer from "../../../../common/components/Spacer";
import { Stack, Typography } from "~/common/mui";
import classNames from "classnames";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useTranslation } from "react-i18next";

import { C_ON_SUCCESS_CONTAINER, C_ON_SURFACE_LOW_EMPHASIS } from "~/common/components/icon/Colors";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";

interface OrderTotalsProps {
  children?: ReactNode;
  totalValue: PriceData | null;
  surcharge?: PriceData;
  weight?: WeightData;
  className?: string;
  hideTaxesMsg?: boolean;
  additionalMsg?: ReactNode;
  taxInNewLine?: boolean;
  customTaxMsg?: ReactNode;
  rows?: {
    label: ReactNode;
    value: ReactNode;
  }[];
  showPriceOnRequest: boolean;
  tariffSurcharge?: boolean;
  totalDiscount?: PriceData | null;
}

interface OrderTotalRowProps {
  value: PriceData | null;
  hideTaxMsg?: boolean;
  taxInNewLine?: boolean;
  additionalMsg?: ReactNode;
  customTaxMsg?: ReactNode;
  showPriceOnRequest: boolean;
  hasSurcharge?: boolean;
  tariffSurcharge?: boolean;
}

const BigText: FC<JSX.IntrinsicElements["span"]> = ({ className, ...rest }) => (
  <span className={classNames("body-large f-bold", className)} {...rest} />
);

const Row: FC<JSX.IntrinsicElements["div"]> = ({ className, ...rest }) => (
  <div className={classNames("d-flex flex-justify-between", className)} {...rest} />
);

export const OrderTotalRow: FC<OrderTotalRowProps> = ({
  value,
  hideTaxMsg,
  taxInNewLine,
  additionalMsg,
  customTaxMsg,
  showPriceOnRequest,
  hasSurcharge,
  tariffSurcharge,
}) => {
  const { t } = useTranslation();

  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;
  const message =
    showPriceOnRequest && !userHasPermission(Permission.ADD_TO_QUOTE) && !webservicesDisabled
      ? t("product.priceOnRequest")
      : "-";

  return (
    <Row className={"mb-2 mt-0 flex-direction-column"}>
      <div className={"d-flex flex-justify-between mb-1"}>
        <div className={"price_tax_wrapper"}>
          <Typography component={"b"} variant={"titleLarge"}>
            {t("checkoutPage.totalPrice")}
          </Typography>
          {additionalMsg && <div>{additionalMsg}</div>}
        </div>
        <Typography component={"b"} variant={"titleLarge"}>
          {value ? PriceUtils.text2(value, message) : "..."}
          {!!(value && (hasSurcharge || tariffSurcharge)) && "*"}
        </Typography>
      </div>
      {!hideTaxMsg && (
        <Typography color={C_ON_SURFACE_LOW_EMPHASIS}>
          {customTaxMsg ? customTaxMsg : t("checkoutPage.total.taxes")}
        </Typography>
      )}
    </Row>
  );
};

const TotalsRow = (props: { label: ReactNode; value: ReactNode }) => (
  <div className="scha__order-totals__row">
    §<div className="mr-2 scha__order-totals__row__label">{props.label}</div>
    <div className="scha__order-totals__row__value">{props.value}</div>
  </div>
);

/**
 * @deprecated Use `OrderSummary` from `~/features/cart/cartV2/OrderSummary` instead.
 * This component will be removed once all consumers are migrated.
 */
const OrderTotals: FC<OrderTotalsProps> = (props) => {
  const {
    totalValue,
    children,
    className,
    weight,
    hideTaxesMsg,
    taxInNewLine,
    rows,
    additionalMsg,
    customTaxMsg,
    showPriceOnRequest,
    surcharge,
    tariffSurcharge,
    totalDiscount,
  } = props;
  const { t } = useTranslation();

  const hasSurcharge = surcharge !== undefined;

  return (
    <div className={className}>
      {weight !== undefined && (
        <Row className={"mb-2 mt-0"}>
          <Typography>{t("cartPage.totalWeight")}</Typography>
          <Typography data-test-id={"total-weight"}>{WeightUtils.text(weight)}</Typography>
        </Row>
      )}
      {hasSurcharge && (
        <Row className={"mb-2 mt-0"}>
          <Typography>{t("page.cart.totals.surcharge")}</Typography>
          <Typography data-test-id={"total-weight"}>{PriceUtils.text(surcharge)}</Typography>
        </Row>
      )}
      {totalDiscount && totalDiscount.value > 0 && (
        <Row className={"mb-2 mt-0"}>
          <Stack direction={"row"} gap={2}>
            <Typography variant="bodyLarge">{t("checkoutPage.totalDiscount")}</Typography>
            <SaleTag size={24} />
          </Stack>
          <Typography color={C_ON_SUCCESS_CONTAINER} component={"b"}>
            {PriceUtils.text2(totalDiscount, "")}
          </Typography>
        </Row>
      )}
      <OrderTotalRow
        hideTaxMsg={hideTaxesMsg}
        taxInNewLine={taxInNewLine}
        value={totalValue}
        additionalMsg={additionalMsg}
        customTaxMsg={customTaxMsg}
        showPriceOnRequest={showPriceOnRequest}
        hasSurcharge={hasSurcharge}
        tariffSurcharge={tariffSurcharge}
      />
      {rows && (
        <>
          {rows.map((row) => (
            <TotalsRow value={row.value} label={row.label} />
          ))}
        </>
      )}
      {children && (
        <>
          <Spacer className={"mt-3 mb-30"} />
          <Stack className={"d-flex flex-justify-end"} gap={4}>
            {children}
          </Stack>
        </>
      )}
    </div>
  );
};

export default memo(OrderTotals);
